import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import {
  Sparkles,
  Play,
  ArrowRight,
  Check,
  Star,
  Zap,
  Video,
  Wand2,
  Image,
  Mic,
  Share2,
  Palette,
  Scissors,
  Users,
  TrendingUp,
  MessageSquare,
  Clock,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import api from "../lib/api";

// TikTok Logo Component
const TikTokLogo = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const InstagramLogo = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

// Animated counter hook
const useCounter = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration]);
  return count;
};

export default function LandingPage() {
  const navigate = useNavigate();
  const videoCount = useCounter(50000, 2500);
  const creatorCount = useCounter(10000, 2500);
  
  // Demo modal state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoVideos, setDemoVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [loadingDemos, setLoadingDemos] = useState(false);
  
  // Fetch demo videos when modal opens
  const fetchDemoVideos = async () => {
    setLoadingDemos(true);
    try {
      const response = await api.get("/demo-videos?limit=6");
      setDemoVideos(response.data?.videos || []);
    } catch (error) {
      console.error("Failed to fetch demo videos:", error);
      // Fallback demo data if API fails
      setDemoVideos([]);
    }
    setLoadingDemos(false);
  };
  
  const openDemoModal = () => {
    setShowDemoModal(true);
    fetchDemoVideos();
  };
  
  const closeDemoModal = () => {
    setShowDemoModal(false);
    setCurrentVideoIndex(0);
  };
  
  const nextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % demoVideos.length);
  };
  
  const prevVideo = () => {
    setCurrentVideoIndex((prev) => (prev - 1 + demoVideos.length) % demoVideos.length);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white overflow-x-hidden font-['Manrope',sans-serif]">
      {/* Ambient Background Glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <motion.div 
          animate={{ 
            x: [0, 50, 0], 
            y: [0, 30, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-[#FF0055]/15 rounded-full blur-[150px]" 
        />
        <motion.div 
          animate={{ 
            x: [0, -30, 0], 
            y: [0, -50, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-[-20%] right-[-10%] w-[700px] h-[700px] bg-[#00F0FF]/15 rounded-full blur-[180px]" 
        />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0B]/60 backdrop-blur-xl backdrop-saturate-150 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center shadow-lg shadow-[#FF0055]/20">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold font-['Outfit',sans-serif] bg-gradient-to-r from-[#00F0FF] to-[#FF0055] bg-clip-text text-transparent">
                iGoViral
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                className="text-zinc-400 hover:text-white hover:bg-white/5 font-medium"
                data-testid="nav-login-btn"
              >
                Log In
              </Button>
              <Button
                onClick={() => navigate("/register")}
                className="bg-gradient-to-r from-[#FF0055] to-[#FF0055]/80 hover:scale-105 text-white font-bold rounded-full px-6 py-2.5 shadow-[0_0_20px_rgba(255,0,85,0.4)] transition-transform duration-300"
                data-testid="nav-signup-btn"
              >
                Start Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-36 pb-24 px-6 md:px-12 lg:px-24">
        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF88] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF88]"></span>
              </span>
              <span className="text-sm text-zinc-400 font-medium">Over {videoCount.toLocaleString()}+ videos created</span>
            </motion.div>

            {/* Main Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black font-['Outfit',sans-serif] tracking-tighter leading-none mb-8">
              <span className="text-white">Stop Scrolling.</span>
              <br />
              <span className="bg-gradient-to-r from-[#00F0FF] via-[#A855F7] to-[#FF0055] bg-clip-text text-transparent">
                Start Creating.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              The all-in-one AI platform that turns your ideas into viral TikToks, Reels, and UGC ads. 
              <span className="text-white font-medium"> No editing skills needed.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Button
                onClick={() => navigate("/register")}
                size="lg"
                className="bg-gradient-to-r from-[#FF0055] to-[#FF0055]/80 hover:scale-105 text-white font-bold rounded-full px-10 py-7 text-lg shadow-[0_0_30px_rgba(255,0,85,0.4)] transition-transform duration-300"
                data-testid="hero-cta-btn"
              >
                Create Your First Video Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={openDemoModal}
                className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-full px-8 py-7 text-lg font-medium transition-colors duration-300"
                data-testid="hero-demo-btn"
              >
                <Play className="w-5 h-5 mr-2 fill-white" />
                Watch Demo
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#00FF88]" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#00FF88]" />
                <span>Free videos included</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#00FF88]" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </motion.div>

          {/* Fake UI Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-20 relative"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent z-10 pointer-events-none h-full" />
            <div className="relative bg-[#121214] rounded-3xl border border-white/10 overflow-hidden shadow-2xl shadow-[#FF0055]/5">
              {/* Window Header */}
              <div className="flex items-center gap-2 px-6 py-4 border-b border-white/5 bg-[#0A0A0B]/50">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#28C840]" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1.5 rounded-full bg-white/5 text-xs text-zinc-500">
                    igoviralcreator.io/video-studio
                  </div>
                </div>
              </div>
              {/* Content Preview */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 p-4 sm:p-6">
                {/* Left Panel - Tools */}
                <div className="hidden sm:block sm:col-span-2 space-y-2">
                  {[Scissors, Wand2, Palette, Mic, Image].map((Icon, i) => (
                    <div key={i} className={`p-3 rounded-xl ${i === 1 ? 'bg-[#00F0FF]/20 border border-[#00F0FF]/30' : 'bg-white/5'} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${i === 1 ? 'text-[#00F0FF]' : 'text-zinc-500'}`} />
                    </div>
                  ))}
                </div>
                {/* Mobile Tools Row */}
                <div className="sm:hidden flex justify-center gap-2 mb-2">
                  {[Scissors, Wand2, Palette, Mic, Image, Sparkles].map((Icon, i) => (
                    <div key={i} className={`p-2 rounded-lg ${i === 1 ? 'bg-[#00F0FF]/20 border border-[#00F0FF]/30' : 'bg-white/5'} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${i === 1 ? 'text-[#00F0FF]' : 'text-zinc-500'}`} />
                    </div>
                  ))}
                </div>
                {/* Main Preview */}
                <div className="col-span-1 sm:col-span-7 bg-black/50 rounded-2xl overflow-hidden relative aspect-video">
                  <img 
                    src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=srgb&fm=jpg&w=1200" 
                    alt="AI Generated Content"
                    className="w-full h-full object-cover opacity-80"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white fill-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                    <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                      <div className="h-full w-[65%] bg-gradient-to-r from-[#00F0FF] to-[#FF0055] rounded-full" />
                    </div>
                  </div>
                </div>
                {/* Right Panel - AI Features */}
                <div className="col-span-1 sm:col-span-3 space-y-2 sm:space-y-3">
                  <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-[#FF0055]/10 to-transparent border border-[#FF0055]/20">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-[#FF0055]" />
                      <span className="text-[10px] sm:text-xs font-bold text-[#FF0055]">AI MAGIC</span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-zinc-400">Background removed successfully</p>
                  </div>
                  <div className="p-3 sm:p-4 rounded-xl bg-white/5 border border-white/5">
                    <p className="text-[10px] sm:text-xs text-zinc-500 mb-1 sm:mb-2">Voice Clone</p>
                    <div className="flex gap-0.5 sm:gap-1">
                      {[...Array(20)].map((_, i) => (
                        <div key={i} className="w-0.5 sm:w-1 bg-[#00F0FF]" style={{ height: `${Math.random() * 15 + 5}px` }} />
                      ))}
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 rounded-xl bg-white/5 border border-white/5">
                    <p className="text-[10px] sm:text-xs text-zinc-500 mb-1 sm:mb-2">Auto Captions</p>
                    <div className="space-y-1">
                      <div className="h-1.5 sm:h-2 bg-white/10 rounded w-full" />
                      <div className="h-1.5 sm:h-2 bg-white/10 rounded w-3/4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-12 px-6 border-y border-white/5 bg-[#121214]/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-20">
            <div className="text-center">
              <div className="text-4xl font-black font-['Outfit',sans-serif] text-white">{creatorCount.toLocaleString()}+</div>
              <div className="text-sm text-zinc-500">Creators</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black font-['Outfit',sans-serif] text-white">{videoCount.toLocaleString()}+</div>
              <div className="text-sm text-zinc-500">Videos Created</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black font-['Outfit',sans-serif] text-white">4.9</div>
              <div className="text-sm text-zinc-500 flex items-center gap-1">
                <Star className="w-3 h-3 text-[#FFD700] fill-[#FFD700]" /> Rating
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-500">Works with</span>
              <div className="flex gap-2">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                  <TikTokLogo className="w-5 h-5 text-white" />
                </div>
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                  <InstagramLogo className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento Features Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4 block">POWERFUL FEATURES</span>
            <h2 className="text-4xl sm:text-5xl font-black font-['Outfit',sans-serif] tracking-tight mb-4">
              Everything You Need to
              <span className="bg-gradient-to-r from-[#00F0FF] to-[#FF0055] bg-clip-text text-transparent"> Dominate</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
              Four powerful studios. One platform. Unlimited viral potential.
            </p>
          </motion.div>

          {/* Bento Grid */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6"
          >
            {/* UGC Ad Studio - Large Card */}
            <motion.div 
              variants={itemVariants}
              className="col-span-1 md:col-span-2 lg:col-span-8 lg:row-span-2 group"
            >
              <div 
                onClick={() => navigate("/register")}
                className="h-full bg-[#121214] rounded-3xl border border-white/5 hover:border-[#FF0055]/30 transition-all duration-300 overflow-hidden cursor-pointer relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF0055]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="grid md:grid-cols-2 h-full">
                  <div className="p-8 lg:p-10 flex flex-col justify-center relative z-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF0055]/10 border border-[#FF0055]/20 text-[#FF0055] text-xs font-bold tracking-wider uppercase mb-4 w-fit">
                      <Sparkles className="w-3 h-3" /> MOST POPULAR
                    </div>
                    <h3 className="text-3xl lg:text-4xl font-black font-['Outfit',sans-serif] text-white mb-4">
                      UGC Ad Studio
                    </h3>
                    <p className="text-zinc-400 text-lg mb-6 leading-relaxed">
                      Create high-converting UGC ads with AI actors. Perfect for e-commerce, dropshipping, and brand marketing. 
                      <span className="text-white font-medium"> No actors. No filming. Just results.</span>
                    </p>
                    <ul className="space-y-3">
                      {["AI Virtual Actors", "Product Binding", "Reaction Videos", "Voice Cloning"].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-zinc-300">
                          <div className="w-5 h-5 rounded-full bg-[#FF0055]/20 flex items-center justify-center">
                            <Check className="w-3 h-3 text-[#FF0055]" />
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="relative h-64 md:h-auto">
                    <img 
                      src="https://images.pexels.com/photos/8117901/pexels-photo-8117901.jpeg?auto=compress&cs=tinysrgb&w=800"
                      alt="UGC Creator"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#121214] via-[#121214]/50 to-transparent" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Video Studio - Tall Card */}
            <motion.div 
              variants={itemVariants}
              className="col-span-1 lg:col-span-4 lg:row-span-2 group"
            >
              <div 
                onClick={() => navigate("/register")}
                className="h-full min-h-[320px] lg:min-h-[400px] bg-[#121214] rounded-3xl border border-white/5 hover:border-[#00F0FF]/30 transition-all duration-300 overflow-hidden cursor-pointer p-6 lg:p-10 relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 h-full flex flex-col">
                  <div className="w-14 h-14 rounded-2xl bg-[#00F0FF]/10 flex items-center justify-center mb-6">
                    <Video className="w-7 h-7 text-[#00F0FF]" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-black font-['Outfit',sans-serif] text-white mb-3">
                    Video Studio
                  </h3>
                  <p className="text-zinc-400 mb-6 flex-grow">
                    Professional editing suite with 19+ tools. Trim, merge, add effects, remove backgrounds, and more.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[Scissors, Wand2, Palette, Image, Mic, Sparkles].map((Icon, i) => (
                      <div key={i} className="aspect-square rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#00F0FF]/10 transition-colors">
                        <Icon className="w-5 h-5 text-zinc-400" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Viral Maker - Phone Mockup */}
            <motion.div 
              variants={itemVariants}
              className="col-span-1 lg:col-span-6 group"
            >
              <div 
                onClick={() => navigate("/register")}
                className="h-full bg-[#121214] rounded-3xl border border-white/5 hover:border-[#A855F7]/30 transition-all duration-300 overflow-hidden cursor-pointer p-6 lg:p-8 relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#A855F7]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="flex items-center gap-4 lg:gap-8 relative z-10">
                  <div className="flex-1">
                    <div className="w-12 h-12 rounded-xl bg-[#A855F7]/10 flex items-center justify-center mb-4">
                      <TrendingUp className="w-6 h-6 text-[#A855F7]" />
                    </div>
                    <h3 className="text-2xl font-black font-['Outfit',sans-serif] text-white mb-2">
                      Viral Maker
                    </h3>
                    <p className="text-zinc-400">
                      Story mode with AI voiceover, trending music, and auto-captions. Go viral on autopilot.
                    </p>
                  </div>
                  {/* Phone Mockup */}
                  <div className="hidden sm:block w-32 h-56 rounded-[28px] border-4 border-zinc-800 bg-black overflow-hidden relative shadow-xl">
                    <img 
                      src="https://images.unsplash.com/photo-1596346599094-4dfa5c61fd0d?crop=entropy&cs=srgb&fm=jpg&w=400"
                      alt="TikTok"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 bg-white/30 rounded-full" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Image Studio */}
            <motion.div 
              variants={itemVariants}
              className="col-span-1 lg:col-span-6 group"
            >
              <div 
                onClick={() => navigate("/register")}
                className="h-full bg-[#121214] rounded-3xl border border-white/5 hover:border-[#FFD700]/30 transition-all duration-300 overflow-hidden cursor-pointer p-6 lg:p-8 relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="flex items-center gap-4 lg:gap-6 relative z-10">
                  <div className="flex-1">
                    <div className="w-12 h-12 rounded-xl bg-[#FFD700]/10 flex items-center justify-center mb-4">
                      <Image className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <h3 className="text-2xl font-black font-['Outfit',sans-serif] text-white mb-2">
                      Image Studio
                    </h3>
                    <p className="text-zinc-400">
                      AI-powered thumbnails, backgrounds, and graphics. Stand out in the feed.
                    </p>
                  </div>
                  <div className="hidden sm:grid grid-cols-2 gap-2">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FFD700]/20 to-[#FF0055]/20" />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* More Features Grid */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#121214]/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-black font-['Outfit',sans-serif] tracking-tight mb-4">
              Plus Everything Else You Need
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[
              { icon: MessageSquare, title: "Script Doctor", desc: "AI script optimization" },
              { icon: Mic, title: "Voice Cloning", desc: "Clone your voice" },
              { icon: Share2, title: "TikTok Posting", desc: "One-click publish" },
              { icon: Palette, title: "Brand Kit", desc: "Save brand assets" },
              { icon: Users, title: "AI Actors", desc: "Virtual presenters" },
              { icon: Clock, title: "Scheduling", desc: "Post at peak times" },
              { icon: TrendingUp, title: "Trend Analysis", desc: "Find viral topics" },
              { icon: Wand2, title: "AI Captions", desc: "Auto-generated" },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-4 sm:p-5 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 hover:bg-white/[0.04] transition-all duration-300"
              >
                <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-400 mb-2 sm:mb-3" />
                <h3 className="font-bold text-white text-sm sm:text-base mb-1">{feature.title}</h3>
                <p className="text-xs sm:text-sm text-zinc-500">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center p-10 sm:p-14 rounded-3xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/10 relative overflow-hidden"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[200px] bg-gradient-to-r from-[#FF0055]/10 to-[#00F0FF]/10 blur-3xl" />
            <div className="relative">
              <div className="flex justify-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-[#FFD700] fill-[#FFD700]" />
                ))}
              </div>
              <blockquote className="text-xl sm:text-2xl text-white/90 mb-8 italic leading-relaxed">
                &ldquo;I went from struggling to post once a week to creating daily viral content. iGoViral&apos;s UGC Studio alone has 10x&apos;d my ad performance. The AI is unreal.&rdquo;
              </blockquote>
              <div className="flex items-center justify-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055] p-0.5">
                  <div className="w-full h-full rounded-full bg-[#0A0A0B]" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-white">Sarah Chen</div>
                  <div className="text-zinc-500 text-sm">E-commerce Brand Owner</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative text-center p-12 sm:p-16 rounded-3xl overflow-hidden"
          >
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF0055] via-[#A855F7] to-[#00F0FF] opacity-20" />
            <div className="absolute inset-0 bg-[#0A0A0B]/90" />
            
            {/* Glow Effects */}
            <div className="absolute top-0 left-1/4 w-[300px] h-[300px] bg-[#FF0055]/30 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-[#00F0FF]/30 rounded-full blur-[100px]" />

            <div className="relative">
              <h2 className="text-4xl sm:text-5xl font-black font-['Outfit',sans-serif] tracking-tight mb-6 text-white">
                Ready to Go Viral?
              </h2>
              <p className="text-zinc-300 mb-10 max-w-xl mx-auto text-lg">
                Join {creatorCount.toLocaleString()}+ creators using AI to dominate social media. Start free, upgrade when you&apos;re ready.
              </p>

              <Button
                onClick={() => navigate("/register")}
                size="lg"
                className="bg-white text-[#0A0A0B] hover:bg-white/90 hover:scale-105 font-bold rounded-full px-10 py-7 text-lg transition-all duration-300"
                data-testid="final-cta-btn"
              >
                Start Creating Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <div className="flex items-center justify-center gap-8 mt-10 text-sm text-zinc-400">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00FF88]" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00FF88]" />
                  <span>Free videos included</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00FF88]" />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold font-['Outfit',sans-serif] text-white">iGoViral</span>
            </div>

            <div className="flex items-center gap-8 text-sm text-zinc-500">
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/pricing" className="hover:text-white transition-colors">Pricing</a>
            </div>

            <div className="text-sm text-zinc-600">
              © 2026 iGoViral Content Creator
            </div>
          </div>
        </div>
      </footer>
      
      {/* Demo Video Modal */}
      <AnimatePresence>
        {showDemoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={closeDemoModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative w-full max-w-5xl bg-[#121214] rounded-3xl border border-white/10 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center">
                    <Play className="w-5 h-5 text-white fill-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white font-['Outfit',sans-serif]">
                      Platform Demo
                    </h3>
                    <p className="text-xs text-zinc-500">
                      See what creators are making with iGoViral
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeDemoModal}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              
              {/* Video Player Area */}
              <div className="relative">
                {loadingDemos ? (
                  <div className="aspect-video flex items-center justify-center bg-black/50">
                    <div className="text-center">
                      <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin mx-auto mb-3" />
                      <p className="text-zinc-400">Loading demo videos...</p>
                    </div>
                  </div>
                ) : demoVideos.length > 0 ? (
                  <div className="relative">
                    {/* Current Video */}
                    <div className="aspect-video bg-black">
                      <video
                        key={demoVideos[currentVideoIndex]?.id}
                        src={demoVideos[currentVideoIndex]?.video_url}
                        className="w-full h-full object-contain"
                        controls
                        autoPlay
                        playsInline
                      />
                    </div>
                    
                    {/* Video Info Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-bold"
                            style={{ 
                              backgroundColor: `${demoVideos[currentVideoIndex]?.badge_color}20`,
                              color: demoVideos[currentVideoIndex]?.badge_color 
                            }}
                          >
                            {demoVideos[currentVideoIndex]?.category}
                          </span>
                          <span className="text-white font-medium">
                            {demoVideos[currentVideoIndex]?.title}
                          </span>
                        </div>
                        <span className="text-zinc-400 text-sm">
                          {currentVideoIndex + 1} / {demoVideos.length}
                        </span>
                      </div>
                    </div>
                    
                    {/* Navigation Arrows */}
                    {demoVideos.length > 1 && (
                      <>
                        <button
                          onClick={prevVideo}
                          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors"
                        >
                          <ChevronLeft className="w-6 h-6 text-white" />
                        </button>
                        <button
                          onClick={nextVideo}
                          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors"
                        >
                          <ChevronRight className="w-6 h-6 text-white" />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="aspect-video flex items-center justify-center bg-black/50">
                    <div className="text-center px-8">
                      <Video className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <h4 className="text-xl font-bold text-white mb-2">
                        Demo Coming Soon
                      </h4>
                      <p className="text-zinc-400 mb-6 max-w-md">
                        Our creators are busy making amazing content. Sign up now to be among the first to create viral videos!
                      </p>
                      <Button
                        onClick={() => {
                          closeDemoModal();
                          navigate("/register");
                        }}
                        className="bg-gradient-to-r from-[#FF0055] to-[#FF0055]/80 text-white rounded-full px-8"
                      >
                        Start Creating Free
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Video Thumbnails */}
              {demoVideos.length > 1 && (
                <div className="p-4 border-t border-white/5">
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {demoVideos.map((video, index) => (
                      <button
                        key={video.id}
                        onClick={() => setCurrentVideoIndex(index)}
                        className={`flex-shrink-0 relative rounded-xl overflow-hidden transition-all ${
                          index === currentVideoIndex 
                            ? "ring-2 ring-[#00F0FF] ring-offset-2 ring-offset-[#121214]" 
                            : "opacity-60 hover:opacity-100"
                        }`}
                      >
                        <div className="w-28 h-16 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center overflow-hidden">
                          {video.thumbnail_url ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${video.thumbnail_url}`}
                              alt={video.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`absolute inset-0 flex items-center justify-center ${video.thumbnail_url ? 'hidden' : ''}`}>
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: `${video.badge_color}30` }}
                            >
                              <Play className="w-4 h-4 fill-current" style={{ color: video.badge_color }} />
                            </div>
                          </div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/10 transition-colors">
                          <Play className="w-4 h-4 text-white fill-white opacity-70" />
                        </div>
                        <span 
                          className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
                          style={{ 
                            backgroundColor: `${video.badge_color}40`,
                            color: video.badge_color 
                          }}
                        >
                          {video.category}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Modal Footer CTA */}
              <div className="px-6 py-4 bg-gradient-to-r from-[#FF0055]/10 to-[#00F0FF]/10 border-t border-white/5">
                <div className="flex items-center justify-between">
                  <p className="text-zinc-400 text-sm">
                    Ready to create videos like these?
                  </p>
                  <Button
                    onClick={() => {
                      closeDemoModal();
                      navigate("/register");
                    }}
                    className="bg-gradient-to-r from-[#FF0055] to-[#FF0055]/80 text-white rounded-full"
                  >
                    Start Creating Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
